import { AuditedEntity, TextListItem } from "app/shared/model/app.model";
import { HREFTemplated, Self, SelfCreatedUpdatedBy, SelfTemplated } from "app/shared/model/link.model";
import { HasId, NameDescSortId } from "app/shared/model/properties.model";
import { BaseEntityProjectionType, TemplateProjections } from "app/shared/model/request-parameter.model";
import { ChoiceList, ChoiceListItem } from "ng/src/app/choice-lists/shared/choice-list.model";
import { LogService } from "ng/src/app/logs/log.service";
import { Note } from "ng/src/app/notes/shared/note.model";
import { Nullish } from "ng/src/app/shared/model/app.model";
import { TagValue } from "ng/src/app/tags/tag.model";
import { LogBookUser, LogUser, LogUserGroup } from "ng/src/app/users/shared/user.model";

export class Template {
	static isType<T extends TemplateTypeId>(template: Template, type: T): template is Template<T> {
		return template.templateType.id === type;
	}
	
	private constructor() {}
}

export interface Template<T extends TemplateTypeId = TemplateTypeId> extends AuditedEntity {
	allowAllUsers: boolean;
	allowSubmitIncomplete: boolean;
	carryForward: boolean;
	commentsToShow?: number;
	endCarryForwardDays?: number;
	enterNotes: boolean;
	evident: boolean;
	hasSubTemplates: boolean;
	id: number;
	isDefaultTemplate: boolean;
	logId: number;
	maxNoteHeight?: number;
	name: string;
	numSegments: number;
	requireCommentOnAbnormal?: boolean;
	templateType: TypedTemplateType<T>
	turnover: boolean;
	walkdown: boolean;
	notifyDefaultCoCreators?: boolean;
	_links: SelfCreatedUpdatedBy & {
		segments: HREFTemplated;
		defaultCoCreators: HREFTemplated;
		log: HREFTemplated;
		templateType: HREFTemplated;
		allowedUsers: HREFTemplated;
	};
}

export interface TemplateWithUsers<T extends TemplateTypeId = TemplateTypeId> extends Template<T> {
	allowedUsers: LogUser[];
	defaultCoCreators: LogUser[];
}

export interface TemplateWithSubTemplates<T extends TemplateTypeId = TemplateTypeId> extends Template<T> {
	subTemplates: Pick<TemplateSegment, 'id' | 'name' | 'type'>[]
}

export interface TemplateWithDrafts<T extends TemplateTypeId = TemplateTypeId> extends TemplateWithUsers<T> {
	drafts?: Note.Projections.KeyNoteTemplateValues[]
}

export class TemplateSegment {
	static isType<T extends TemplateSegmentTypeId>(segment: Nullish<TemplateSegment>, type: T): segment is TypedTemplateSegment<T> {
		if (!segment)
			return false;
		
		return segment.type.id === type;
	}
	
	static typePredicate<T extends TemplateSegmentTypeId>(type: T): (segment: Nullish<TemplateSegment>) => segment is TypedTemplateSegment<T> {
		return (segment: Nullish<TemplateSegment>): segment is TypedTemplateSegment<T> => {
			return TemplateSegment.isType(segment, type);
		}
	}
	
	private constructor() {}
}

export interface TemplateSegment<T extends TemplateSegmentTypeId = TemplateSegmentTypeId> extends AuditedEntity {
	logId: number;
	aboveMaxAlertMessage?: string;
	allowAnyUser?: boolean;
	allowReject?: boolean;
	allowUndo?: boolean;
	barcode?: boolean;
	belowMinAlertMessage?: string;
	calculate?: boolean;
	choiceList?: ChoiceList;
	clearTime?: boolean;
	decimalPlaces?: number | null;
	defaultChoice?: ChoiceListItem;
	depth?: number;
	formula?: string;
	height?: number;
	id: number;
	includeDate?: boolean;
	includeTime?: boolean;
	key?: boolean;
	level?: number;
	longName: string;
	maxAllowed?: number;
	maxValue?: number;
	minRequired?: number;
	minValue?: number;
	name: string;
	obsolete: boolean;
	print: boolean;
	requireCommentOnAbnormal?: boolean;
	required?: boolean;
	sortOrder: number;
	tagValue?: TagValue;
	templateId: number;
	text?: string;
	textAlignment?: 'LEFT' | 'CENTER' | 'RIGHT';
	textArea?: boolean;
	textList: TextListItem[];
	textOption?: 'PLAIN_TEXT_ONLY' | 'ALLOW_RICH_TEXT' | 'DEFAULT_TO_RICH_TEXT';
	turnover?: boolean;
	type: TypedTemplateSegmentType<T>;
	units?: string;
	users: LogUser[];
	variable?: boolean;
	width?: number;
	sendPendingSignoffNotification?: boolean;
	altText?: string;
	requireOnShift?: boolean;
	requireAllSignoffs?: boolean;
	templateUsersCanEdit?: boolean;
	userGroups?: LogUserGroup[],
	editUsers: LogUser[];
	_links: SelfCreatedUpdatedBy & {
		choiceList: HREFTemplated;
		defaultChoice: HREFTemplated;
		tagValue: HREFTemplated;
		template: HREFTemplated;
		templateSegment?: HREFTemplated;
		type: HREFTemplated;
		users: HREFTemplated;
	};
}

export type TemplateSegmentUpdate = Partial<Omit<{
	[K in keyof TemplateSegment<any>]: K extends HasId ? string : TemplateSegment<any>[K]
}, '_links' | 'id'> & {
	prevItemId: number | string
}>

export interface TemplateType<T extends TemplateTypeId = any> extends NameDescSortId {
	id: T
	attributes: TemplateTypeAttributes;
	_links: Self & {
		templateType: HREFTemplated;
	};
}

export interface TemplateSegmentType<T extends TemplateSegmentTypeId = TemplateSegmentTypeId> extends NameDescSortId {
	id: T,
	properties: TemplateSegmentTypeProperties,
	_links: SelfTemplated
}

export type TemplateSegmentTypeLink = TemplateSegmentType & {
	_links: {
		templateSegmentType: HREFTemplated;
	};
}

interface TemplateTypeAttributes {
	ALLOW_SUBMIT_INCOMPLETE?: boolean;
	SECTIONAL?: boolean;
	TABULAR?: boolean;
	TURNOVER?: boolean;
}

interface TemplateSegmentTypeProperties {
	CALCULATE?: boolean;
	KEY?: boolean;
	NAME? : boolean;
	REQUIRED_RANGE_INPUT?: boolean;
	SIMPLE_REQUIRED_INPUT?: boolean;
	USERS?: boolean;
 }

 export enum TemplateSegmentTypeId {
	LITERAL_TEXT = 1,
	TAG_CHOICE = 2,
	TAG = 3,
	TEXT_INPUT = 4,
	SUB_TEMPLATE = 5,
	CHECKBOX = 6,
	FILE_ATTACHMENT = 7,
	TEXT_CHOICE = 8,
	NUMERIC_INPUT = 9,
	DATE_TIME = 10,
	USER_CHOOSER = 11,
	SIGNOFF = 12,
	SIGNATURE = 13
}

export const TemplateSegmentTypeOrder = Object.freeze([
	TemplateSegmentTypeId.NUMERIC_INPUT,
	TemplateSegmentTypeId.TEXT_CHOICE,
	TemplateSegmentTypeId.TEXT_INPUT,
	TemplateSegmentTypeId.DATE_TIME,
	TemplateSegmentTypeId.TAG_CHOICE,
	TemplateSegmentTypeId.LITERAL_TEXT,
	TemplateSegmentTypeId.TAG,
	TemplateSegmentTypeId.SIGNATURE,
	TemplateSegmentTypeId.SIGNOFF,
	TemplateSegmentTypeId.CHECKBOX,
	TemplateSegmentTypeId.USER_CHOOSER,
	TemplateSegmentTypeId.FILE_ATTACHMENT,
]);

export enum TemplateTypeId {
	OPERATIONAL = 1,
	INSPECTION = 2
}

export interface TemplateProjectionType extends Record<TemplateProjections, Template>, BaseEntityProjectionType {
	[TemplateProjections.BASE]: Template
	[TemplateProjections.WITH_SUB_TEMPLATES]: TemplateWithSubTemplates;
	[TemplateProjections.WITH_USERS]: TemplateWithUsers;
}

export module TemplateWithUsers {
	export function currentUserCanAccess(template: TemplateWithUsers) {
		const currentUser = LogService.selectedLog?.activeLogUser;
		return TemplateWithUsers.userCanAccess(template, currentUser ?? null);
	}
	
	export function userCanAccess(template: TemplateWithUsers, user: LogBookUser | LogUser | null) {
		user = !user ? null : ('firstName' in user) ? user : user.user;
		return template.allowAllUsers || (user && template.allowedUsers.some(tUser => tUser.user.id === user!.id));
	}
}

export interface TemplateCopyOverride {
	choiceList?: number | 'COPY' | null;
	tagValue?: number | null;
	defaultChoice?: number | null;
}


type TypedTemplateTypeBase<T extends TemplateTypeId, K extends keyof TemplateTypeAttributes> = Omit<TemplateType<T>, 'attributes'> & {
	attributes: {
		[U in K]: true
	}
}

export type TypedTemplateType<T extends TemplateTypeId = TemplateTypeId> = (
	T extends TemplateTypeId.OPERATIONAL ? TypedTemplateTypeBase<T, never> :
	T extends TemplateTypeId.INSPECTION ? TypedTemplateTypeBase<T, 'TURNOVER' | 'ALLOW_SUBMIT_INCOMPLETE' | 'TABULAR' | 'SECTIONAL'> :
	TemplateType
)


type TypedSegmentBase<T extends TemplateSegmentTypeId, OKey extends keyof TemplateSegment> = {
	[U in keyof Omit<TemplateSegment, 'type'> as TemplateSegment[U] extends Required<TemplateSegment>[U] ? U : never]-?: TemplateSegment[U]
} & Pick<TemplateSegment<T>, OKey> & { type: TypedTemplateSegmentType<T> }

export type TypedTemplateSegment<T extends TemplateSegmentTypeId = TemplateSegmentTypeId> = (
	T extends TemplateSegmentTypeId.LITERAL_TEXT ? TypedSegmentBase<T, 'text'> :
	T extends TemplateSegmentTypeId.TAG_CHOICE ? TypedSegmentBase<T, 'tagValue' | 'depth' | 'minRequired' | 'maxAllowed'> :
	T extends TemplateSegmentTypeId.TAG ? TypedSegmentBase<T, 'tagValue'> :
	T extends TemplateSegmentTypeId.TEXT_INPUT ? TypedSegmentBase<T, 'textOption' | 'width' | 'height' | 'textArea' | 'barcode' | 'textAlignment'> :
	T extends TemplateSegmentTypeId.SUB_TEMPLATE ? TypedSegmentBase<T, 'level' | 'print' | 'requireCommentOnAbnormal' | 'turnover' | 'clearTime'> :
	T extends TemplateSegmentTypeId.FILE_ATTACHMENT ? TypedSegmentBase<T, 'minRequired' | 'maxAllowed'> :
	T extends TemplateSegmentTypeId.TEXT_CHOICE ? TypedSegmentBase<T, 'choiceList' | 'defaultChoice'> :
	T extends TemplateSegmentTypeId.NUMERIC_INPUT ? TypedSegmentBase<T, 'units' | 'minValue' | 'maxValue' | 'belowMinAlertMessage' | 'aboveMaxAlertMessage'> :
	T extends TemplateSegmentTypeId.DATE_TIME ? TypedSegmentBase<T, 'includeDate' | 'includeTime'> :
	T extends TemplateSegmentTypeId.SIGNOFF ? TypedSegmentBase<T, 'text' | 'altText' | 'sendPendingSignoffNotification' |
			'requireOnShift' | 'requireAllSignoffs' | 'templateUsersCanEdit' | 'userGroups' | 'users' | 'editUsers' |
			'level' | 'allowAnyUser' | 'allowReject' | 'allowUndo'> :
	T extends TemplateSegmentTypeId.SIGNATURE ? TypedSegmentBase<T, 'textList' | 'text'> :
	TemplateSegment
)


type TypedSegmentPropertyBase<T extends TemplateSegmentTypeId, K extends keyof TemplateSegmentTypeProperties> = Omit<TemplateSegmentType<T>, 'properties'> & {
	properties: {
		[U in K]: true
	}
};

export type TypedTemplateSegmentType<T extends TemplateSegmentTypeId = TemplateSegmentTypeId> = (
	T extends TemplateSegmentTypeId.LITERAL_TEXT ? TypedSegmentPropertyBase<T, never> :
	T extends TemplateSegmentTypeId.TAG_CHOICE ? TypedSegmentPropertyBase<T, 'NAME' | 'REQUIRED_RANGE_INPUT' | 'KEY' | 'CALCULATE'> :
	T extends TemplateSegmentTypeId.TAG ? TypedSegmentPropertyBase<T, never> :
	T extends TemplateSegmentTypeId.TEXT_INPUT ? TypedSegmentPropertyBase<T, 'NAME' | 'SIMPLE_REQUIRED_INPUT' | 'CALCULATE'> :
	T extends TemplateSegmentTypeId.SUB_TEMPLATE ? TypedSegmentPropertyBase<T, 'NAME'> :
	T extends TemplateSegmentTypeId.CHECKBOX ? TypedSegmentPropertyBase<T, 'NAME' | 'SIMPLE_REQUIRED_INPUT' | 'CALCULATE'> :
	T extends TemplateSegmentTypeId.FILE_ATTACHMENT ? TypedSegmentPropertyBase<T, 'NAME' | 'REQUIRED_RANGE_INPUT'> :
	T extends TemplateSegmentTypeId.TEXT_CHOICE ? TypedSegmentPropertyBase<T, 'NAME' | 'SIMPLE_REQUIRED_INPUT' | 'KEY' | 'CALCULATE'> :
	T extends TemplateSegmentTypeId.NUMERIC_INPUT ? TypedSegmentPropertyBase<T, 'NAME' | 'SIMPLE_REQUIRED_INPUT' | 'CALCULATE'> :
	T extends TemplateSegmentTypeId.DATE_TIME ? TypedSegmentPropertyBase<T, 'NAME' | 'SIMPLE_REQUIRED_INPUT' | 'CALCULATE'> :
	T extends TemplateSegmentTypeId.USER_CHOOSER ? TypedSegmentPropertyBase<T, 'NAME' | 'SIMPLE_REQUIRED_INPUT' | 'KEY'> :
	T extends TemplateSegmentTypeId.SIGNOFF ? TypedSegmentPropertyBase<T, 'NAME' | 'USERS'> :
	T extends TemplateSegmentTypeId.SIGNATURE ? TypedSegmentPropertyBase<T, 'NAME' | 'SIMPLE_REQUIRED_INPUT'> :
	TemplateSegmentType
);